/**
 * i18n 域 Zod 共享契约 — 通用多语言翻译端点（POST /api/v1/i18n/translate）
 *
 * 全站 MultiLangFormRows「从英文翻译」按钮（分类/标签/FAQ/推送模板/BONUS 原因/Banner/推荐区块/付费墙文案）
 * 统一走本端点：源默认英文、目标为该行语言，真调 DeepL（Key 取自 deepl_credentials 全局凭证）。
 * 与 books/translate 解耦（域中立）。admin-own 契约，dev-docs 收录义务 #229。
 */

import { z } from 'zod';

/**
 * POST /i18n/translate 入参：批量把 texts 从 source_locale（省略=DeepL 自动检测）译到 target_locale。
 * texts 上限 50（逐行按钮通常发 1 条；预留批量）；locale 为 ISO 639-1（可带 region/script 子标签，如 pt-BR）。
 */
export const TranslateI18nRequestSchema = z.object({
  texts: z.array(z.string()).min(1).max(50),
  source_locale: z.string().min(2).optional(),
  target_locale: z.string().min(2),
});
export type TranslateI18nRequest = z.infer<typeof TranslateI18nRequestSchema>;

/** 翻译结果：逐条译文（顺序对应入参 texts）+ 检测到的源语言（ISO 639-1 小写，可空）*/
export type TranslateI18nResponse = {
  translations: string[];
  detected_source_lang: string | null;
};

/**
 * ADM-OPS-S010 运营配置自动翻译闭集 = 86 §1.3 文案 cell 登记表模块全集。
 */
export const CONFIG_TRANSLATION_MODULES = [
  'home_sections',
  'faq',
  'categories',
  'tags',
  'product_titles',
  'push_templates',
] as const;
export type ConfigTranslationTaskModule = (typeof CONFIG_TRANSLATION_MODULES)[number];

/** ADM-OPS-S010 task matrix DTO — execution lease fields intentionally stay server-only. */
export type ConfigTranslationTaskResponse = {
  id: string;
  module: ConfigTranslationTaskModule;
  target_language: string;
  trigger_source: 'CONFIG_CREATED' | 'CONTENT_CHANGED' | 'LANGUAGE_ENABLED' | 'MATRIX_COMPLETION';
  generation: number;
  status: 'pending' | 'success' | 'failed';
  failure_reason: string | null;
  missing_cell_count: number;
  attempt_count: number;
  last_attempted_at: string | null;
  updated_at: string;
};
